import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import {
  nombreCliente,
  crearClienteConEventos,
  crearClientesEnLote,
  ligarEventosACliente,
  marcarEventosSinFicha,
  nuevoIdContacto,
  patchContactos,
} from "../../utils/clientes";
import {
  agruparEventosSinFicha,
  posiblesDuplicados,
  requiereRevision,
  resumenBackfill,
} from "../../utils/clientesBackfill";
import { ClienteFormFields, FORM_CLIENTE_VACIO } from "./ClienteCreateDialog";
import {
  XMarkIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  ArrowPathRoundedSquareIcon,
  LinkIcon,
} from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

const fmtDate = (ts, tz) => {
  const d = ts?.toDate ? ts.toDate() : ts ? new Date(ts) : null;
  if (!d || isNaN(d)) return "Sin fecha";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric", timeZone: tz });
};

const datosAForm = (datos) => ({ ...FORM_CLIENTE_VACIO, ...(datos || {}) });

const MOTIVO_TEXTO = {
  correo: "tiene el mismo correo",
  telefono: "tiene el mismo teléfono",
  nombre: "se llama igual",
  empresa: "tiene la misma empresa",
};

// Asistente para armar el directorio de clientes de un venue que ya venía
// operando (FRA-354). La ficha de cliente nace hacia adelante —al convertir
// un lead, al crear un evento—, así que un venue con años de historia abre
// el Directorio y lo encuentra vacío, con todo lo que sabe de sus clientes
// atrapado en los campos sueltos de cada evento.
//
// Aquí se recorre al revés: se agrupan los eventos que parecen de la misma
// persona y el owner confirma. El agrupado es, de paso, la respuesta a
// "¿quién me ha repetido?", que hoy no se puede contestar.
//
// Dos velocidades a propósito: los que piden criterio —recurrentes, los que
// quizá ya tienen ficha, los sospechosos de haberse juntado de más— pasan
// uno por uno; los de un solo evento, que son la mayoría, se despachan en
// una sola tabla. Recorrer 120 personas de una en una no lo hace
// nadie.
//
// Se guarda grupo por grupo, no al final: cada ficha creada saca a sus
// eventos de la lista de pendientes, así que salirse a la mitad y volver
// después retoma exactamente donde iba.
const ClientesWizard = ({ open, onClose }) => {
  const navigate = useNavigate();
  const { selectedVenueId, events, clientes, calendarTimezone } = useVenue();
  const { userInfo, user } = useAuth();

  const [fase, setFase] = useState("portada");
  const [saltados, setSaltados] = useState([]);
  const [excluidos, setExcluidos] = useState([]);
  const [separado, setSeparado] = useState(false);
  const [deseleccionados, setDeseleccionados] = useState([]);
  const [form, setForm] = useState(FORM_CLIENTE_VACIO);
  // "Son personas distintas": en vez de descartar el correo/teléfono que no
  // se eligió, la ficha nace con los dos contactos.
  const [contactosAparte, setContactosAparte] = useState(false);
  const [rellenar, setRellenar] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hechos, setHechos] = useState({ fichas: 0, eventos: 0 });

  const grupos = useMemo(
    () => agruparEventosSinFicha(events, clientes),
    [events, clientes]
  );
  const resumen = useMemo(() => resumenBackfill(grupos), [grupos]);
  const duplicados = useMemo(() => posiblesDuplicados(clientes), [clientes]);

  const revisables = useMemo(
    () => grupos.filter(requiereRevision).filter((g) => !saltados.includes(g.id)),
    [grupos, saltados]
  );
  const sueltos = useMemo(
    () => grupos.filter((g) => !requiereRevision(g)).filter((g) => !saltados.includes(g.id)),
    [grupos, saltados]
  );
  const actual = revisables[0] || null;

  const seleccionados = useMemo(
    () => sueltos.filter((g) => !deseleccionados.includes(g.id)),
    [sueltos, deseleccionados]
  );

  // El componente vive montado aunque esté cerrado: al reabrirlo se empieza
  // de cero, si no el contador y los saltados sobreviven a la sesión pasada.
  useEffect(() => {
    if (open) {
      setFase("portada");
      setSaltados([]);
      setDeseleccionados([]);
      setHechos({ fichas: 0, eventos: 0 });
      setRellenar(true);
    }
  }, [open]);

  useEffect(() => {
    setForm(datosAForm(actual?.datos));
    setExcluidos([]);
    setSeparado(false);
    setContactosAparte(false);
  }, [actual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Adelantar sin efectos: cuando ya no queda nada que revisar la fase
  // efectiva pasa sola al lote, y de ahí al cierre.
  const faseEfectiva = useMemo(() => {
    if (fase === "revisar" && !actual) return sueltos.length ? "lote" : "cierre";
    if (fase === "lote" && !sueltos.length) return "cierre";
    return fase;
  }, [fase, actual, sueltos.length]);

  if (!open) return null;

  const eventosElegidos = actual
    ? actual.eventos.filter((e) => !excluidos.includes(e.id)).map((e) => e.id)
    : [];

  // Un contacto por cada combinación que los eventos traían. Se emparejan por
  // posición —el primer correo con el primer teléfono— porque es lo más
  // cercano a la verdad sin preguntar quién es quién; el owner los edita
  // después en la ficha, que es donde tiene los nombres.
  const contactosDeVariantes = (() => {
    const correos = actual?.variantes?.correo || [];
    const telefonos = actual?.variantes?.telefono || [];
    const cuantos = Math.max(correos.length, telefonos.length, 1);
    const lista = Array.from({ length: cuantos }, (_, i) => ({
      id: nuevoIdContacto(),
      // El primero se queda con el nombre capturado; los demás nacen sin
      // nombre y con su correo como etiqueta, para que se note cuál falta.
      nombre: i === 0 ? form.nombre : "",
      apellido: i === 0 ? form.apellido : "",
      correo: correos[i] || "",
      telefono: telefonos[i] || "",
      principal: i === 0,
    }));
    return patchContactos(lista, { principalId: lista[0]?.id });
  })();
  const fichaExistente = separado ? null : actual?.fichaExistente;

  const empezar = () => setFase(revisables.length ? "revisar" : sueltos.length ? "lote" : "cierre");

  const sumar = (fichas, eventos) =>
    setHechos((h) => ({ fichas: h.fichas + fichas, eventos: h.eventos + eventos }));

  const crear = async () => {
    if (!actual || !eventosElegidos.length) return;
    setSaving(true);
    try {
      const clienteId = await crearClienteConEventos({
        venueId: selectedVenueId,
        datos: contactosAparte ? { ...form, ...contactosDeVariantes } : form,
        eventIds: eventosElegidos,
        user,
        rellenarCampos: rellenar,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.CLIENTE_CREATE,
          entity: "cliente",
          entityId: clienteId,
          entityName: nombreCliente(form),
          details: { origen: "asistente", eventosLigados: eventosElegidos.length },
        },
        userInfo,
        user
      );
      sumar(1, eventosElegidos.length);
    } catch (err) {
      showAlert(`No se pudo crear el cliente. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const ligar = async () => {
    if (!actual || !fichaExistente || !eventosElegidos.length) return;
    setSaving(true);
    try {
      await ligarEventosACliente({
        venueId: selectedVenueId,
        cliente: fichaExistente.cliente,
        eventIds: eventosElegidos,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.EVENT_CLIENTE_LINK,
          entity: "cliente",
          entityId: fichaExistente.cliente.id,
          entityName: nombreCliente(fichaExistente.cliente),
          details: { origen: "asistente", eventosLigados: eventosElegidos.length },
        },
        userInfo,
        user
      );
      sumar(0, eventosElegidos.length);
    } catch (err) {
      showAlert(`No se pudieron ligar los eventos. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const noEsCliente = async () => {
    if (!actual) return;
    // Solo se descartan los eventos que siguen marcados: los que el owner
    // desmarcó no son de este "cliente" y vuelven a la lista, como promete
    // el texto de arriba. Antes se marcaban los del grupo completo.
    if (!eventosElegidos.length) {
      showAlert(
        "Desmarcaste todos los eventos: no hay nada que descartar. Vuelve a marcar los que no sean de un cliente.",
        "error"
      );
      return;
    }
    setSaving(true);
    try {
      await marcarEventosSinFicha({
        venueId: selectedVenueId,
        eventIds: eventosElegidos,
      });
    } catch (err) {
      showAlert(`No se pudo descartar. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const crearLote = async () => {
    if (!seleccionados.length) return;
    setSaving(true);
    try {
      const creados = await crearClientesEnLote({
        venueId: selectedVenueId,
        grupos: seleccionados.map((g) => ({
          datos: g.datos,
          eventIds: g.eventos.map((e) => e.id),
        })),
        user,
        // El lote no tiene formulario: la ficha se crea con lo que el evento
        // ya dice, así que no hay corrección que escribirle de vuelta.
        rellenarCampos: false,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.CLIENTE_BACKFILL,
          entity: "cliente",
          entityId: "",
          entityName: `${creados.length} clientes`,
          details: {
            fichas: creados.length,
            eventos: creados.reduce((s, c) => s + c.eventos, 0),
          },
        },
        userInfo,
        user
      );
      sumar(creados.length, creados.reduce((s, c) => s + c.eventos, 0));
      setFase("cierre");
    } catch (err) {
      showAlert(`No se pudieron crear los clientes. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const toggleEvento = (id) =>
    setExcluidos((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleSuelto = (id) =>
    setDeseleccionados((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const irAFicha = (id) => {
    onClose?.();
    navigate(`/dashboard/clientes/${id}`);
  };

  const totalRevisables = revisables.length + hechos.fichas;
  const progreso = totalRevisables > 0 ? hechos.fichas / totalRevisables : 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[92vh]">
        <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
              Armar el directorio de clientes
            </h2>
            {faseEfectiva === "revisar" && (
              <span className="ml-auto text-xs text-zinc-400">
                {hechos.fichas} de {totalRevisables}
              </span>
            )}
            <button
              type="button"
              onClick={onClose}
              className={`text-zinc-400 hover:text-zinc-600 ${faseEfectiva === "revisar" ? "" : "ml-auto"}`}
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {faseEfectiva === "revisar" && hechos.fichas > 0 && (
            <div className="mt-2 h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${Math.round(progreso * 100)}%`, backgroundColor: ACCENT }}
              />
            </div>
          )}
        </div>

        {/* ── Portada ────────────────────────────────────────────── */}
        {faseEfectiva === "portada" && (
          <>
            <div className="p-6 overflow-y-auto">
              {resumen.personas === 0 ? (
                <div className="text-center py-6">
                  <CheckCircleIcon className="h-10 w-10 mx-auto text-emerald-500" />
                  <div className="mt-3 text-lg font-semibold text-zinc-900 dark:text-white">
                    Todos tus eventos ya tienen cliente
                  </div>
                  <p className="mt-1 text-sm text-zinc-500">
                    No hay nada que armar. De aquí en adelante el cliente se crea solo
                    al convertir un lead o al dar de alta un evento.
                  </p>
                </div>
              ) : (
                <>
                  <div className="flex items-start gap-3">
                    <div
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg"
                      style={{ backgroundColor: ACCENT + "1a" }}
                    >
                      <UserGroupIcon className="h-5 w-5" style={{ color: ACCENT }} />
                    </div>
                    <div>
                      <div className="text-lg font-semibold text-zinc-900 dark:text-white">
                        {resumen.eventos} {resumen.eventos === 1 ? "evento" : "eventos"} sin cliente
                      </div>
                      <p className="text-sm text-zinc-500 mt-1">
                        Cada uno registra a quién le vendiste, pero por separado. El
                        asistente los agrupa por persona: esos {resumen.eventos} eventos son
                        de{" "}
                        <strong className="text-zinc-700 dark:text-zinc-300">
                          {resumen.personas} {resumen.personas === 1 ? "cliente" : "clientes"}
                        </strong>
                        .
                      </p>
                    </div>
                  </div>

                  {resumen.recurrentes > 0 && (
                    <div className="mt-5 rounded-lg border border-indigo-200 bg-indigo-50 dark:bg-indigo-500/10 dark:border-indigo-500/20 px-4 py-3 flex items-start gap-3">
                      <ArrowPathRoundedSquareIcon className="h-5 w-5 shrink-0 text-indigo-500" />
                      <div>
                        <div className="text-sm font-semibold text-indigo-900 dark:text-indigo-200">
                          {resumen.recurrentes}{" "}
                          {resumen.recurrentes === 1 ? "de ellos ya te repitió" : "de ellos ya te repitieron"}
                        </div>
                        <div className="text-xs text-indigo-700 dark:text-indigo-300 mt-0.5">
                          Entre {resumen.recurrentes === 1 ? "ese cliente y sus" : "esos clientes suman"}{" "}
                          {resumen.eventosRecurrentes} de los {resumen.eventos} eventos. Hoy el
                          panel no puede decírtelo porque cada evento guarda los datos de su
                          cliente por separado.
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="mt-4 rounded-lg bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3">
                    <div className="text-xs font-medium uppercase tracking-wide text-zinc-400 mb-2">
                      Cómo se van a repartir
                    </div>
                    <div className="text-sm text-zinc-600 dark:text-zinc-400 space-y-1.5">
                      {resumen.revisables > 0 && (
                        <div>
                          <strong className="text-zinc-800 dark:text-zinc-200">
                            {resumen.revisables}{" "}
                            {resumen.revisables === 1 ? "cliente" : "clientes"} ·{" "}
                            {resumen.eventosRevisables}{" "}
                            {resumen.eventosRevisables === 1 ? "evento" : "eventos"}
                          </strong>{" "}
                          — pasan uno por uno para que los confirmes.
                        </div>
                      )}
                      {resumen.sueltos > 0 && (
                        <div>
                          <strong className="text-zinc-800 dark:text-zinc-200">
                            {resumen.sueltos} {resumen.sueltos === 1 ? "cliente" : "clientes"} ·{" "}
                            {resumen.eventosSueltos}{" "}
                            {resumen.eventosSueltos === 1 ? "evento" : "eventos"}
                          </strong>{" "}
                          — uno cada uno, nada que decidir: se crean todos juntos al final.
                        </div>
                      )}
                    </div>
                  </div>

                  {(resumen.porNombre > 0 || resumen.sospechosos > 0) && (
                    <div className="mt-3 space-y-2">
                      {resumen.porNombre > 0 && (
                        <p className="text-xs text-zinc-500 bg-zinc-50 dark:bg-zinc-800/40 rounded-lg px-3 py-2">
                          {resumen.porNombre === 1
                            ? "Uno de esos grupos se armó"
                            : `${resumen.porNombre} de esos grupos se armaron`}{" "}
                          solo por el nombre, porque esos eventos no traen correo ni
                          teléfono. Siempre pasan por tu revisión: nunca se crean sin que
                          los veas.
                        </p>
                      )}
                      {resumen.sospechosos > 0 && (
                        <p className="text-xs text-amber-700 bg-amber-50 dark:bg-amber-500/10 rounded-lg px-3 py-2">
                          {resumen.sospechosos === 1
                            ? "Uno reúne eventos"
                            : `${resumen.sospechosos} reúnen eventos`}{" "}
                          a nombre de personas distintas, o muchos eventos bajo un nombre
                          genérico. Al revisarlos vas a poder separarlos.
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              {resumen.personas === 0 && duplicados.length > 0 && (
                <ListaDuplicados duplicados={duplicados} onIr={irAFicha} />
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button type="button" onClick={onClose} className="px-3 py-2 text-sm text-zinc-500 hover:text-zinc-700">
                {resumen.personas === 0 ? "Cerrar" : "Ahora no"}
              </button>
              {resumen.personas > 0 && (
                <button
                  type="button"
                  onClick={empezar}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
                  style={{ backgroundColor: ACCENT }}
                >
                  Empezar
                  <ArrowRightIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          </>
        )}

        {/* ── Uno por uno ────────────────────────────────────────── */}
        {faseEfectiva === "revisar" && actual && (
          <>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-lg font-semibold text-zinc-900 dark:text-white">
                    {nombreCliente(actual.datos)}
                  </span>
                  {actual.esRecurrente && (
                    <span className="rounded-md bg-indigo-100 dark:bg-indigo-500/20 px-2 py-0.5 text-xs font-medium text-indigo-700 dark:text-indigo-300">
                      {actual.eventos.length} eventos
                    </span>
                  )}
                </div>
                <div className="text-sm text-zinc-500">
                  {[actual.datos.correo, actual.datos.telefono].filter(Boolean).join(" · ") ||
                    "Sin datos de contacto"}
                </div>
              </div>

              {actual.sospechoso && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 px-4 py-3 flex items-start gap-3">
                  <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-500" />
                  <div className="text-xs text-amber-800 dark:text-amber-300">
                    {actual.porNombre ? (
                      <>
                        Son {actual.eventos.length} eventos agrupados únicamente porque están
                        al mismo nombre, sin correo ni teléfono que lo respalde. Si{" "}
                        <strong>{nombreCliente(actual.datos)}</strong> es un nombre genérico
                        y no una persona, desmarca abajo los que no le correspondan: vuelven
                        a la lista y los revisas por separado.
                      </>
                    ) : (
                      <>
                        Estos eventos comparten correo o teléfono, pero están a nombre de{" "}
                        {actual.nombresDistintos.length} personas distintas. Si fue un planner
                        quien reservó para varios clientes, desmarca abajo los que no sean de{" "}
                        <strong>{nombreCliente(actual.datos)}</strong>: vuelven a la lista y
                        los revisas por separado.
                      </>
                    )}
                  </div>
                </div>
              )}

              {actual.porNombre && !actual.sospechoso && (
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3 text-xs text-zinc-600 dark:text-zinc-400">
                  Estos eventos se agruparon porque están al mismo nombre: no comparten
                  correo ni teléfono. Revísalos antes de confirmar y desmarca los que sean
                  de otra persona.
                </div>
              )}

              {fichaExistente && (
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/40 px-4 py-3">
                  <div className="text-sm text-zinc-700 dark:text-zinc-300">
                    {fichaExistente.seguro ? "Ya tienes a " : "¿Es "}
                    <strong>{nombreCliente(fichaExistente.cliente)}</strong>
                    {fichaExistente.seguro
                      ? ` en el directorio: ${MOTIVO_TEXTO[fichaExistente.motivo]}.`
                      : `, que ya tienes en el directorio? ${
                          MOTIVO_TEXTO[fichaExistente.motivo].charAt(0).toUpperCase() +
                          MOTIVO_TEXTO[fichaExistente.motivo].slice(1)
                        }, pero el correo y el teléfono no coinciden.`}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSeparado(true)}
                    className="mt-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-700 underline"
                  >
                    No es la misma persona, crear un cliente nuevo
                  </button>
                </div>
              )}

              {!fichaExistente && (
                <>
                  <Variantes
                    variantes={actual.variantes}
                    form={form}
                    onElegir={(campo, valor) => setForm((f) => ({ ...f, [campo]: valor }))}
                    contactosAparte={contactosAparte}
                    onSepararContactos={setContactosAparte}
                  />
                  {contactosAparte && (
                    <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 bg-white dark:bg-zinc-800/40 px-3 py-2 text-xs text-zinc-600 dark:text-zinc-400">
                      El cliente nace con{" "}
                      <strong>{contactosDeVariantes.contactos.length} contactos</strong>. El
                      de abajo es el principal; los demás quedan con su correo y teléfono y
                      les pones nombre desde su página.
                    </div>
                  )}
                  <ClienteFormFields form={form} setForm={setForm} />
                </>
              )}

              <div>
                <div className="text-xs font-medium text-zinc-500 mb-2">
                  {actual.eventos.length === 1
                    ? "Su evento"
                    : `Sus ${actual.eventos.length} eventos — desmarca los que no sean suyos`}
                </div>
                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 divide-y divide-zinc-100 dark:divide-zinc-700/40">
                  {actual.eventos.map((e) => (
                    <label
                      key={e.id}
                      className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                    >
                      <input
                        type="checkbox"
                        checked={!excluidos.includes(e.id)}
                        onChange={() => toggleEvento(e.id)}
                        className="mt-0.5 h-4 w-4 rounded border-zinc-300 accent-[#1b4d2e]"
                      />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm text-zinc-900 dark:text-white truncate">
                          {e.nombre}
                          {e.status === "cancelado" && (
                            <span className="ml-1.5 text-xs text-zinc-400">(cancelado)</span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-500 truncate">
                          {fmtDate(e.startTime, calendarTimezone)}
                          {actual.eventos.length > 1 && e.cliente ? ` · a nombre de ${e.cliente}` : ""}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {!fichaExistente && (
                <label className="flex items-start gap-2 text-xs text-zinc-500 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={rellenar}
                    onChange={(e) => setRellenar(e.target.checked)}
                    className="mt-0.5 h-3.5 w-3.5 rounded border-zinc-300 accent-[#1b4d2e]"
                  />
                  <span>
                    Corregir también los datos en sus eventos. Si arreglaste algo arriba,
                    el evento queda igual que el cliente. Apágalo cuando el contacto del
                    evento era de verdad otra persona.
                  </span>
                </label>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button
                type="button"
                onClick={noEsCliente}
                disabled={saving}
                className="px-3 py-2 rounded-lg text-sm font-medium border border-zinc-300 dark:border-zinc-600 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 disabled:opacity-50"
                title="Un apartado interno, una prueba: no es de ningún cliente y no se vuelve a preguntar"
              >
                No es un cliente
              </button>
              <button
                type="button"
                onClick={() => setSaltados((s) => [...s, actual.id])}
                className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Saltar
              </button>
              <button
                type="button"
                onClick={fichaExistente ? ligar : crear}
                disabled={saving || !eventosElegidos.length}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {saving ? (
                  "Guardando…"
                ) : fichaExistente ? (
                  <>
                    <LinkIcon className="h-4 w-4" />
                    Ligar a su cliente
                  </>
                ) : (
                  <>
                    Crear y ligar
                    <ArrowRightIcon className="h-4 w-4" />
                  </>
                )}
              </button>
            </div>
          </>
        )}

        {/* ── Lote ───────────────────────────────────────────────── */}
        {faseEfectiva === "lote" && (
          <>
            <div className="p-5 overflow-y-auto space-y-3">
              <div>
                <div className="text-sm font-semibold text-zinc-900 dark:text-white">
                  {sueltos.length} {sueltos.length === 1 ? "cliente" : "clientes"} de un solo evento
                </div>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Un evento, una persona: no hay nada que decidir. Desmarca los que no
                  quieras y crea el resto de una vez.
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setDeseleccionados([])}
                  className="text-xs font-medium text-zinc-500 hover:text-zinc-700"
                >
                  Marcar todos
                </button>
                <button
                  type="button"
                  onClick={() => setDeseleccionados(sueltos.map((g) => g.id))}
                  className="text-xs font-medium text-zinc-500 hover:text-zinc-700"
                >
                  Desmarcar todos
                </button>
                <span className="ml-auto text-xs text-zinc-400">
                  {seleccionados.length} seleccionados
                </span>
              </div>

              <div className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 divide-y divide-zinc-100 dark:divide-zinc-700/40 max-h-[45vh] overflow-y-auto">
                {sueltos.map((g) => (
                  <label
                    key={g.id}
                    className="flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800/40"
                  >
                    <input
                      type="checkbox"
                      checked={!deseleccionados.includes(g.id)}
                      onChange={() => toggleSuelto(g.id)}
                      className="mt-0.5 h-4 w-4 rounded border-zinc-300 accent-[#1b4d2e]"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                        {nombreCliente(g.datos)}
                      </div>
                      <div className="text-xs text-zinc-500 truncate">
                        {[g.datos.correo, g.datos.telefono].filter(Boolean).join(" · ") || "Sin contacto"}
                      </div>
                    </div>
                    <div className="text-xs text-zinc-400 text-right shrink-0 max-w-[40%]">
                      <div className="truncate">{g.eventos[0].nombre}</div>
                      <div>{fmtDate(g.eventos[0].startTime, calendarTimezone)}</div>
                    </div>
                  </label>
                ))}
              </div>

            </div>

            <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button
                type="button"
                onClick={() => setFase("cierre")}
                className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Dejarlos para después
              </button>
              <button
                type="button"
                onClick={crearLote}
                disabled={saving || !seleccionados.length}
                className="ml-auto px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {saving
                  ? "Creando…"
                  : `Crear ${seleccionados.length} ${seleccionados.length === 1 ? "cliente" : "clientes"}`}
              </button>
            </div>
          </>
        )}

        {/* ── Cierre ─────────────────────────────────────────────── */}
        {faseEfectiva === "cierre" && (
          <>
            <div className="p-6 overflow-y-auto">
              <div className="text-center">
                <CheckCircleIcon className="h-10 w-10 mx-auto text-emerald-500" />
                <div className="mt-3 text-lg font-semibold text-zinc-900 dark:text-white">
                  {hechos.fichas > 0 || hechos.eventos > 0 ? "Directorio armado" : "No quedó nada por armar"}
                </div>
                <p className="mt-1 text-sm text-zinc-500">
                  {hechos.fichas > 0 || hechos.eventos > 0 ? (
                    <>
                      Creaste {hechos.fichas} {hechos.fichas === 1 ? "cliente" : "clientes"} y ligaste{" "}
                      {hechos.eventos} {hechos.eventos === 1 ? "evento" : "eventos"}.
                      {resumen.recurrentes > 0 && (
                        <>
                          {" "}
                          Ahora la lista te marca como{" "}
                          <strong className="text-zinc-700 dark:text-zinc-300">Recurrente</strong> a
                          quien ya te repitió.
                        </>
                      )}
                    </>
                  ) : (
                    "Puedes retomarlo cuando quieras desde el aviso del Directorio."
                  )}
                </p>
                {(revisables.length > 0 || sueltos.length > 0) && (
                  <p className="mt-2 text-xs text-zinc-400">
                    Quedaron {revisables.length + sueltos.length} sin cliente. El aviso sigue ahí.
                  </p>
                )}
              </div>

              {duplicados.length > 0 && <ListaDuplicados duplicados={duplicados} onIr={irAFicha} />}
            </div>
            <div className="flex items-center justify-end px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
                style={{ backgroundColor: ACCENT }}
              >
                Listo
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// Cuando los eventos del grupo no dicen lo mismo, el owner elige cuál queda.
// Cuál correo es el bueno lo sabe él, no nosotros.
const CAMPOS_VARIANTES = [
  { campo: "correo", label: "Aparece con dos correos" },
  { campo: "telefono", label: "Aparece con dos teléfonos" },
  { campo: "nombreEmpresa", label: "Aparece con dos empresas" },
];

// Correo y teléfono admiten una salida que empresa no: que las dos versiones
// sean VERDAD, de dos personas distintas del mismo cliente. Es el caso
// ClassEvents —Christopher y Valeria— y antes obligaba a tirar una.
const CAMPOS_CON_CONTACTOS = new Set(["correo", "telefono"]);

function Variantes({ variantes, form, onElegir, contactosAparte, onSepararContactos }) {
  const conVarias = CAMPOS_VARIANTES.filter(({ campo }) => (variantes?.[campo] || []).length > 1);
  if (!conVarias.length) return null;
  return (
    <div className="space-y-2">
      {conVarias.map(({ campo, label }) => {
        const separable = CAMPOS_CON_CONTACTOS.has(campo);
        const separado = separable && contactosAparte;
        return (
          <div key={campo} className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 px-3 py-2">
            <div className="text-xs text-zinc-500 mb-1.5">
              {separado
                ? `${label} — se guardan los dos, como contactos distintos:`
                : `${label} — elige cuál se queda:`}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {variantes[campo].map((v) => (
                <button
                  key={v}
                  type="button"
                  disabled={separado}
                  onClick={() => onElegir(campo, v)}
                  className={`rounded-md border px-2 py-1 text-xs font-medium transition ${
                    separado
                      ? "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-500"
                      : form[campo] === v
                      ? "border-transparent text-white"
                      : "border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50"
                  }`}
                  style={!separado && form[campo] === v ? { backgroundColor: ACCENT } : undefined}
                >
                  {v}
                </button>
              ))}
            </div>
            {separable && (
              <button
                type="button"
                onClick={() => onSepararContactos(!separado)}
                className="mt-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-700 underline"
              >
                {separado
                  ? "No, es la misma persona: elegir uno"
                  : "Son personas distintas del mismo cliente"}
              </button>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Fichas que ya existen y parecen ser la misma persona. Solo se señalan:
// fusionarlas es a mano, desde la ficha que decidas conservar.
function ListaDuplicados({ duplicados, onIr }) {
  return (
    <div className="mt-6 rounded-lg border border-amber-200 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/20 px-4 py-3">
      <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
        {duplicados.length}{" "}
        {duplicados.length === 1 ? "par de clientes parece" : "pares de clientes parecen"} ser la misma
        persona
      </div>
      <div className="text-xs text-amber-700 dark:text-amber-400 mt-0.5 mb-2">
        Se capturaron dos veces. Abre la que quieras conservar, pásale sus eventos desde la
        otra y archiva la que sobra.
      </div>
      <div className="space-y-1">
        {duplicados.slice(0, 8).map(({ a, b, motivo }) => (
          <div key={`${a.id}-${b.id}`} className="text-xs text-amber-900 dark:text-amber-200">
            <button type="button" onClick={() => onIr(a.id)} className="underline font-medium">
              {nombreCliente(a)}
            </button>
            {" y "}
            <button type="button" onClick={() => onIr(b.id)} className="underline font-medium">
              {nombreCliente(b)}
            </button>
            <span className="text-amber-700 dark:text-amber-400"> — {MOTIVO_TEXTO[motivo]}</span>
          </div>
        ))}
        {duplicados.length > 8 && (
          <div className="text-xs text-amber-700 dark:text-amber-400">
            y {duplicados.length - 8} más.
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientesWizard;
